// React
import { useState, useEffect, useRef, useMemo } from 'react';

// Store
import useProfileStore from '../../store/profileStore.js';
import useAthleteStore from '../../store/athleteStore.js';
import useCoachStore from '../../store/coachStore.js';

// Hooks
import { useAppTheme } from '../../hooks/useAppTheme.js';

// Components
import {
    AddOutlined,
    ExpandMoreOutlined,
    RemoveOutlined,
    FitnessCenterOutlined,
    AccessTimeOutlined,
    SpeedOutlined,
    DescriptionOutlined,
    CalendarTodayOutlined,
    ReportProblem as WarningIcon,
    PublicOutlined
} from '@mui/icons-material';
import {
    Box,
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Switch,
    IconButton,
    InputAdornment,
    Typography,
    Tooltip,
    Paper,
    Stack
} from '@mui/material';

import CustomTextField from './TextField.js';
import CustomSlider from './Slider.js';
import { CustomSelect, CustomMenuItem, getMenuProps } from './Select.js';
import TextblockView from '../dialog/TextblockView.js';
import WeeklySummaryOfSelectedAthletes from './WeeklySummaryOfSelectedAthletes.js';

// Style
import { grey, blue, green, yellow, red, orange } from '@mui/material/colors/index.js';
import { darkTheme } from '../../styles/colorSchemes/index.js';

// Utils
import { formatDate, formatWeekday, formatTrainingFocus } from '../../utils/index.js';


// ⭐ Helper: check if date is valid
const isValidDateValue = (value) => {
    if (!value) return false;
    const d = value instanceof Date ? value : new Date(value);
    return !Number.isNaN(d.getTime());
};

// KW -> Date Helpers
function getStartOfISOWeek(year, week) {
    const simple = new Date(year, 0, 4);
    const dow = simple.getDay() || 7; 
    simple.setDate(simple.getDate() - dow + 1 + (week - 1) * 7);
    return simple;
}

function getDateFromISOWeek(year, week, weekday) {
    const base = getStartOfISOWeek(year, week);
    const date = new Date(base);
    date.setDate(base.getDate() + (weekday - 1));
    return date;
}


// --- SUMMARY HEADER ---
const TrainingSessionSummary = ({ trainingSession, index, isDarkMode }) => {
    return (
        <AccordionSummary 
            expandIcon={<ExpandMoreOutlined sx={{ color: isDarkMode ? darkTheme.textSecondary : undefined }} />}
        >
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, // Stack on mobile
                    gap: { xs: 1, sm: 0 },
                    width: '100%',
                    alignItems: 'center'
                }}
            >
                {/* Datum */}
                <Typography sx={{ textAlign: 'left', fontWeight: 500, color: isDarkMode ? darkTheme.textPrimary : undefined }}>
                    {index + 1}. Einheit (
                    {isValidDateValue(trainingSession.date)
                        ? formatDate(trainingSession.date)
                        : formatWeekday(trainingSession.dayOfWeek)}
                    )
                </Typography>

                {/* Fokus */}
                <Typography className="focus-text" sx={{ textAlign: { xs: 'left', sm: 'center' }, color: isDarkMode ? darkTheme.textSecondary : grey[600] }}>
                    {trainingSession.trainingFocus !== ""
                        ? formatTrainingFocus(trainingSession.trainingFocus)
                        : "-----"}
                </Typography>

                {/* Fehlende Felder */}
                <Box sx={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>
                    {(() => {
                        const missing = [];
                        const isTraining = ![7, 8, 9, 10].includes(trainingSession.trainingFocus);

                        if (trainingSession.trainingFocus === "") missing.push("Fokus");
                        if ((trainingSession.description || "").trim() === "") missing.push("Beschreibung");
                        if (isTraining && Number(trainingSession.duration) === 0) missing.push("Dauer");
                        if (isTraining && Number(trainingSession.intensity) === 0) missing.push("Intensität");

                        if (missing.length > 0) {
                            return (
                                <Tooltip
                                    title={
                                        <Typography fontSize="small">
                                            Fehlt: {missing.join(', ')}
                                        </Typography>
                                    }
                                >
                                    <WarningIcon color="error" />
                                </Tooltip>
                            );
                        }
                        return null;
                    })()}
                </Box>
            </Box>
        </AccordionSummary>
    );
};


// --- MAIN COMPONENT ---
const CreateWeeklyScheduleSessions = (props) => {

    const {
        handleChangeTrainingSession,
        handlePutTextblock,
        handleSendTextblock,
        isAthlete,
        setTextblocks,
        textblocks,
        startWeek,
        year
    } = props;

    const {
        removeTrainingSession,
        trainingSessions: rawSessions,
        setTrainingSessions
    } = useAthleteStore();

    const { openTextblockView, setOpenTextblockView } = useCoachStore();
    const { profileType } = useProfileStore();
    const { isDarkMode } = useAppTheme();

    // ⭐ Protection: trainingSessions MUST be an array (memoized to prevent useEffect dependency issues)
    const trainingSessions = useMemo(() => 
        Array.isArray(rawSessions) ? rawSessions : [], 
        [rawSessions]
    );

    const [description] = useState([""]);
    const [textblockId, setTextblockId] = useState([undefined]);
    const [indexShow, setIndexShow] = useState(0);

    const prevWeekRef = useRef(startWeek);
    const prevYearRef = useRef(year);
    const isInitialMount = useRef(true);
    const primaryColor = isDarkMode ? orange[500] : blue[600];

    // --- SHARED STYLES ---
    const commonCardStyle = {
        p: 2.5,
        border: `1px solid ${isDarkMode ? darkTheme.cardBorder : grey[300]}`, 
        borderRadius: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        backgroundColor: isDarkMode ? darkTheme.cardBg : 'white',
        boxShadow: isDarkMode ? '0 2px 4px rgba(0,0,0,0.2)' : '0 2px 4px rgba(0,0,0,0.02)',
        transition: 'all 0.2s ease',
        '&:hover': {
            borderColor: primaryColor,
            boxShadow: isDarkMode ? '0 8px 16px rgba(0,0,0,0.3)' : '0 8px 16px rgba(0,0,0,0.08)'
        }
    };
    
    // New compact style for the first row
    const compactCardStyle = {
        ...commonCardStyle,
        p: { xs: 1.5, sm: 2 },
        height: 'auto',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden'
    };

    const headerBoxStyle = {
        display: 'flex', 
        alignItems: 'center', 
        minHeight: '32px' 
    };
     const headerBoxStyle2 = {
        display: 'flex', 
        alignItems: 'center', 
        minHeight: '32px' 
    };

    // New compact header style for the first row
    const compactHeaderBoxStyle = {
        ...headerBoxStyle,
        mb: 1.5, // Reduced margin bottom
    };

    useEffect(() => {
        const weekChanged = prevWeekRef.current !== startWeek;
        const yearChanged = prevYearRef.current !== year;
        
        if (isInitialMount.current) {
            isInitialMount.current = false;
            if (trainingSessions.length > 0) {
                const needsFix = trainingSessions.some(session => !isValidDateValue(session.date));
                if (needsFix) {
                    const fixedSessions = trainingSessions.map(session => {
                        if (!session || !session.dayOfWeek) return session;
                        if (isValidDateValue(session.date)) return session;
                        const newDate = getDateFromISOWeek(year, startWeek, session.dayOfWeek);
                        return { ...session, date: newDate };
                    });
                    setTrainingSessions(fixedSessions);
                }
            }
            prevWeekRef.current = startWeek;
            prevYearRef.current = year;
            return;
        }
        
        if ((weekChanged || yearChanged) && trainingSessions.length > 0) {
            const updatedSessions = trainingSessions.map(session => {
                if (!session || !session.dayOfWeek) return session;
                const newDate = getDateFromISOWeek(year, startWeek, session.dayOfWeek);
                return { ...session, date: newDate };
            });
            setTrainingSessions(updatedSessions);
        }
        
        prevWeekRef.current = startWeek;
        prevYearRef.current = year;
    }, [startWeek, year, trainingSessions, setTrainingSessions]);


    const addTrainingSessionEntry = () => {
        if (trainingSessions.length > 0) {
            const last = trainingSessions[trainingSessions.length - 1];
            let nextDow = last.dayOfWeek === 7 ? 1 : last.dayOfWeek + 1;
            const nextDate = getDateFromISOWeek(year, startWeek, nextDow);

            const newSession = {
                dayOfWeek: nextDow,
                trainingFocus: "",
                duration: 0,
                description: "",
                intensity: 0,
                date: nextDate,
                isGlobal: false
            };

            setTrainingSessions([...trainingSessions, newSession]);
            return;
        }

        const firstDate = getDateFromISOWeek(year, startWeek, 1);
        const firstSession = {
            dayOfWeek: 1,
            trainingFocus: "",
            duration: 0,
            description: "",
            intensity: 0,
            date: firstDate,
            isGlobal: false
        };

        setTrainingSessions([firstSession]);
    };


    const changeDesc = (value, index) => {
        description[index] = value;
        if (value.length === 0) {
            textblockId[index] = undefined;
        }
        handleChangeTrainingSession(index, value, 'description');
    };


    const handleSelectTextblock = (textblock, index) => {
        textblockId[index] =
            (!textblock.isglobal || profileType === 'nationalCoach')
                ? textblock.textblockid
                : undefined;

        // Set both description and training focus from the textblock
        changeDesc(textblock.textblock, index);
        handleChangeTrainingSession(index, textblock.trainingfocus, 'trainingFocus');
    };

    const handleChange = (index, checkValue) => {
        handleChangeTrainingSession(index, checkValue, 'isGlobal');
    };

    const createTextblock = async (index, checked) => {
        let textblockRet = await handleSendTextblock(index, checked);

        if (textblockRet !== undefined) {
            const textblockIdTmp = textblockId.map((c, i) => {
                if (i === index) {
                    return textblockRet.textblockid;
                } else {
                    return c;
                }
            });

            setTextblockId(textblockIdTmp);
        }
    };

    return (
        <Accordion sx={{ background: isDarkMode ? darkTheme.accordionBg : grey[100], '&.MuiAccordion-root': { mt: 0 }, '&.MuiAccordion-root.Mui-expanded': { mb: '2px', mt: 0 } }} defaultExpanded={true}>
            <AccordionSummary 
                expandIcon={<ExpandMoreOutlined sx={{ color: isDarkMode ? darkTheme.textSecondary : undefined }} />}
                sx={{ '& .MuiAccordionSummary-content': { color: isDarkMode ? darkTheme.textPrimary : undefined } }}
            >
                <Typography sx={{ color: isDarkMode ? darkTheme.textPrimary : undefined }}>Trainingseinheiten</Typography>
            </AccordionSummary>

            <AccordionDetails sx={{ bgcolor: isDarkMode ? darkTheme.headerBg : undefined }}>

                <WeeklySummaryOfSelectedAthletes />

                <Box sx={{ mt: 3 }}>
                    {trainingSessions.map((session, index) => (
                        <Accordion
                            key={index}
                            sx={{ 
                                background: isDarkMode ? darkTheme.cardBg : 'white', 
                                mb: 3, 
                                borderRadius: '12px !important', 
                                border: `1px solid ${isDarkMode ? darkTheme.cardBorder : grey[300]}`, 
                                boxShadow: isDarkMode ? '0 4px 12px rgba(0,0,0,0.2)' : '0 4px 12px rgba(0,0,0,0.05)', 
                                '&:before': { display: 'none' },
                                transition: 'box-shadow 0.2s ease',
                                '&:hover': {
                                    boxShadow: isDarkMode ? '0 6px 16px rgba(0,0,0,0.3)' : '0 6px 16px rgba(0,0,0,0.08)'
                                }
                            }}
                            defaultExpanded={true}
                        >
                            <TrainingSessionSummary
                                trainingSession={session}
                                index={index}
                                isDarkMode={isDarkMode}
                            />

                            <AccordionDetails sx={{ p: { xs: 2, sm: 3 }, bgcolor: isDarkMode ? darkTheme.headerBg : '#fbfbfb', borderTop: `1px solid ${isDarkMode ? darkTheme.cardBorder : grey[200]}` }}>
                                
                                <Stack spacing={3}>
                                    
                                    {/* Top Row: 3-Column Grid */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: { xs: 'column', sm: 'row' },
                                            gap: { xs: 2, sm: 3 },
                                            '& > .MuiPaper-root': {
                                                flex: { xs: 'none', sm: 1 },
                                                minWidth: { xs: '100%', sm: 0 }
                                            }
                                        }}
                                    >
                                        <Paper elevation={0} sx={compactCardStyle}>
                                            <Box sx={compactHeaderBoxStyle}>
                                                <CalendarTodayOutlined sx={{ mr: 1, color: primaryColor, fontSize: '1.25rem' }} />
                                                <Typography variant="body1" sx={{ fontWeight: 600, color: isDarkMode ? darkTheme.textPrimary : grey[700] }}>
                                                    Wochentag
                                                </Typography>
                                            </Box>
                                            
                                            <CustomSelect
                                                value={session.dayOfWeek}
                                                onChange={e => handleChangeTrainingSession(index, e.target.value, 'dayOfWeek')}
                                                size="small"
                                                fullWidth
                                                isDarkMode={isDarkMode}
                                                darkTheme={darkTheme}
                                                primaryColor={primaryColor}
                                                MenuProps={getMenuProps(isDarkMode, darkTheme)}
                                            >
                                                <CustomMenuItem value={1} isDarkMode={isDarkMode} darkTheme={darkTheme} primaryColor={primaryColor}>Montag</CustomMenuItem>
                                                <CustomMenuItem value={2} isDarkMode={isDarkMode} darkTheme={darkTheme} primaryColor={primaryColor}>Dienstag</CustomMenuItem>
                                                <CustomMenuItem value={3} isDarkMode={isDarkMode} darkTheme={darkTheme} primaryColor={primaryColor}>Mittwoch</CustomMenuItem>
                                                <CustomMenuItem value={4} isDarkMode={isDarkMode} darkTheme={darkTheme} primaryColor={primaryColor}>Donnerstag</CustomMenuItem>
                                                <CustomMenuItem value={5} isDarkMode={isDarkMode} darkTheme={darkTheme} primaryColor={primaryColor}>Freitag</CustomMenuItem>
                                                <CustomMenuItem value={6} isDarkMode={isDarkMode} darkTheme={darkTheme} primaryColor={primaryColor}>Samstag</CustomMenuItem>
                                                <CustomMenuItem value={7} isDarkMode={isDarkMode} darkTheme={darkTheme} primaryColor={primaryColor}>Sonntag</CustomMenuItem>
                                            </CustomSelect>
                                        </Paper>

                                        <Paper elevation={0} sx={compactCardStyle}>
                                            <Box sx={compactHeaderBoxStyle}>
                                                <FitnessCenterOutlined sx={{ mr: 1, color: primaryColor, fontSize: '1.25rem' }} />
                                                <Typography variant="body1" sx={{ fontWeight: 600, color: isDarkMode ? darkTheme.textPrimary : grey[700] }}>
                                                    Trainingsfokus
                                                </Typography>
                                            </Box>
                                            
                                            <CustomSelect
                                                value={Number(session.trainingFocus) || ""}
                                                onChange={e => handleChangeTrainingSession(index, Number(e.target.value), 'trainingFocus')}
                                                size="small"
                                                fullWidth
                                                isDarkMode={isDarkMode}
                                                darkTheme={darkTheme}
                                                primaryColor={primaryColor}
                                                MenuProps={getMenuProps(isDarkMode, darkTheme)}
                                            >
                                                {!isAthlete && <CustomMenuItem value={1} isDarkMode={isDarkMode} darkTheme={darkTheme} primaryColor={primaryColor}>Kraft</CustomMenuItem>}
                                                {!isAthlete && <CustomMenuItem value={2} isDarkMode={isDarkMode} darkTheme={darkTheme} primaryColor={primaryColor}>Ausdauer</CustomMenuItem>}
                                                {!isAthlete && <CustomMenuItem value={3} isDarkMode={isDarkMode} darkTheme={darkTheme} primaryColor={primaryColor}>Technik</CustomMenuItem>}
                                                {!isAthlete && <CustomMenuItem value={4} isDarkMode={isDarkMode} darkTheme={darkTheme} primaryColor={primaryColor}>Taktik</CustomMenuItem>}
                                                {!isAthlete && <CustomMenuItem value={5} isDarkMode={isDarkMode} darkTheme={darkTheme} primaryColor={primaryColor}>Koordination</CustomMenuItem>}
                                                {!isAthlete && <CustomMenuItem value={6} isDarkMode={isDarkMode} darkTheme={darkTheme} primaryColor={primaryColor}>Schnelligkeit</CustomMenuItem>}
                                                <CustomMenuItem value={7} isDarkMode={isDarkMode} darkTheme={darkTheme} primaryColor={primaryColor}>Wettkampf/Liga</CustomMenuItem>
                                                <CustomMenuItem value={8} isDarkMode={isDarkMode} darkTheme={darkTheme} primaryColor={primaryColor}>Ruhetag</CustomMenuItem>
                                                <CustomMenuItem value={9} isDarkMode={isDarkMode} darkTheme={darkTheme} primaryColor={primaryColor}>Reisetag</CustomMenuItem>
                                                {isAthlete && <CustomMenuItem value={10} isDarkMode={isDarkMode} darkTheme={darkTheme} primaryColor={primaryColor}>Sonstige Aktivität</CustomMenuItem>}
                                            </CustomSelect>
                                        </Paper>

                                        {!isAthlete && (
                                            <Paper elevation={0} sx={{ 
                                                ...compactCardStyle,
                                                opacity: [7, 8, 9, 10].includes(session.trainingFocus) ? 0.6 : 1
                                            }}>
                                                <Box sx={compactHeaderBoxStyle}>
                                                    <AccessTimeOutlined sx={{ mr: 1, color: [7, 8, 9, 10].includes(session.trainingFocus) ? grey[400] : primaryColor, fontSize: '1.25rem' }} />
                                                    <Typography variant="body1" sx={{ fontWeight: 600, color: isDarkMode ? darkTheme.textPrimary : grey[700] }}>
                                                        Geplante Dauer
                                                    </Typography>
                                                </Box>
                                                
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <IconButton 
                                                        size="small"
                                                        disabled={[7, 8, 9, 10].includes(session.trainingFocus) || session.duration <= 0}
                                                        onClick={() => handleChangeTrainingSession(index, Math.max(0, Number(session.duration) - 1), 'duration')}
                                                        sx={{
                                                            backgroundColor: isDarkMode ? darkTheme.headerBg : grey[200],
                                                            width: 32,
                                                            height: 32,
                                                            color: isDarkMode ? darkTheme.textPrimary : undefined,
                                                            '&:hover': {
                                                                backgroundColor: isDarkMode ? darkTheme.cardHover : grey[300]
                                                            },
                                                            '&.Mui-disabled': {
                                                                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : grey[100],
                                                                color: isDarkMode ? darkTheme.textSecondary : undefined
                                                            }
                                                        }}
                                                    >
                                                        <RemoveOutlined sx={{ fontSize: '1rem' }} />
                                                    </IconButton>
                                                    <CustomTextField
                                                        type="number"
                                                        disabled={[7, 8, 9, 10].includes(session.trainingFocus)}
                                                        value={session.duration}
                                                        onChange={e => handleChangeTrainingSession(index, e.target.value, 'duration')}
                                                        size="small"
                                                        inputProps={{ style: { textAlign: 'center', fontWeight: 700, fontSize: '1.1rem' } }}
                                                        InputProps={{
                                                            endAdornment: <InputAdornment position="end"><Typography sx={{ color: grey[600], fontSize: '0.8rem' }}>Min</Typography></InputAdornment>
                                                        }}
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                // Hide spin buttons for number input
                                                                '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
                                                                    WebkitAppearance: 'none',
                                                                    margin: 0,
                                                                },
                                                                '& input[type=number]': {
                                                                    MozAppearance: 'textfield',
                                                                },
                                                                borderRadius: 2,
                                                                height: '40px', 
                                                                backgroundColor: isDarkMode ? darkTheme.headerBg : 'white',
                                                                '& fieldset': { borderColor: isDarkMode ? darkTheme.cardBorder : 'rgba(0, 0, 0, 0.23)' },
                                                                '&:hover fieldset': { borderColor: primaryColor },
                                                                '&.Mui-focused fieldset': { borderColor: primaryColor }
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                color: isDarkMode ? darkTheme.textPrimary : undefined,
                                                            }
                                                        }}
                                                    />
                                                    <IconButton 
                                                        size="small"
                                                        disabled={[7, 8, 9, 10].includes(session.trainingFocus)}
                                                        onClick={() => handleChangeTrainingSession(index, Number(session.duration) + 1, 'duration')}
                                                        sx={{
                                                            backgroundColor: isDarkMode ? darkTheme.headerBg : grey[200],
                                                            width: 32,
                                                            height: 32,
                                                            color: isDarkMode ? darkTheme.textPrimary : undefined,
                                                            '&:hover': {
                                                                backgroundColor: isDarkMode ? darkTheme.cardHover : grey[300]
                                                            },
                                                            '&.Mui-disabled': {
                                                                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : grey[100],
                                                                color: isDarkMode ? darkTheme.textSecondary : undefined
                                                            }
                                                        }}
                                                    >
                                                        <AddOutlined sx={{ fontSize: '1rem' }} />
                                                    </IconButton>
                                                </Box>
                                            </Paper>
                                        )}
                                    </Box>

                                    {/* Intensity Row (Coach Only) */}
                                    {!isAthlete && (
                                        <Paper elevation={0} sx={{ 
                                            ...commonCardStyle,
                                            opacity: [7, 8, 9, 10].includes(session.trainingFocus) ? 0.6 : 1
                                        }}>
                                            <Box sx={headerBoxStyle2}>
                                                <SpeedOutlined sx={{ mr: 1, color: primaryColor, fontSize: '1.25rem' }} />
                                                <Typography variant="body1" sx={{ fontWeight: 600, color: isDarkMode ? darkTheme.textPrimary : grey[700] }}>
                                                    Geplante Intensität: {session.intensity || 0}/10
                                                </Typography>
                                            </Box>
                                            <Box sx={{ px: { xs: 1, sm: 3 }, pt: 1, pb: 1 }}>
                                                <CustomSlider
                                                    disabled={[7, 8, 9, 10].includes(session.trainingFocus)}
                                                    value={session.intensity || 0}
                                                    step={1}
                                                    min={0}
                                                    max={10}
                                                    marks={[{ value: 0, label: '0' }, { value: 5, label: '5' }, { value: 10, label: '10' }]}
                                                    valueLabelDisplay="auto"
                                                    onChange={(e, val) => handleChangeTrainingSession(index, val, 'intensity')}
                                                    sx={{
                                                        height: 8,
                                                        '& .MuiSlider-thumb': { height: 24, width: 24, backgroundColor: '#fff', border: `3px solid ${primaryColor}`, boxShadow: '0 2px 4px rgba(0,0,0,0.2)', '&:focus, &:hover, &.Mui-active': { boxShadow: `0 0 0 8px ${primaryColor}20` } },
                                                        '& .MuiSlider-track': { border: 'none', height: 8, borderRadius: 4, background: `linear-gradient(90deg, ${green[500]} 0%, ${yellow[500]} 50%, ${red[500]} 100%)`, backgroundSize: session.intensity > 0 ? `${(10 / (session.intensity || 1)) * 100}% 100%` : '0% 0%', backgroundRepeat: 'no-repeat' },
                                                        '& .MuiSlider-rail': { opacity: 1, backgroundColor: isDarkMode ? darkTheme.cardBorder : '#e0e0e0', height: 8, borderRadius: 4 },
                                                        '& .MuiSlider-mark': { display: 'none' },
                                                        '& .MuiSlider-markLabel': { fontSize: '0.75rem', color: isDarkMode ? darkTheme.textSecondary : grey[500], fontWeight: 500, top: 32 },
                                                        '& .MuiSlider-valueLabel': { backgroundColor: primaryColor, borderRadius: '8px', padding: '4px 8px', fontSize: '0.875rem', fontWeight: 600 }
                                                    }}
                                                />
                                            </Box>
                                        </Paper>
                                    )}

                                    {/* Description Row */}
                                    <Paper elevation={0} sx={commonCardStyle}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, minHeight: '32px' }}>
                                            <DescriptionOutlined sx={{ mr: 1, color: primaryColor, fontSize: '1.25rem' }} />
                                            <Typography variant="body1" sx={{ fontWeight: 600, color: isDarkMode ? darkTheme.textPrimary : grey[700] }}>
                                                Beschreibung
                                            </Typography>
                                        </Box>
                                        <CustomTextField
                                            fullWidth
                                            multiline
                                            minRows={4}
                                            variant="outlined"
                                            value={session.description ?? ""}
                                            onChange={e => changeDesc(e.target.value, index)}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 2,
                                                    backgroundColor: isDarkMode ? darkTheme.headerBg : 'white',
                                                    '& fieldset': { borderColor: isDarkMode ? darkTheme.cardBorder : 'rgba(0, 0, 0, 0.23)' },
                                                    '&:hover fieldset': { borderColor: primaryColor },
                                                    '&.Mui-focused fieldset': { borderColor: primaryColor }
                                                },
                                                '& .MuiInputBase-input': {
                                                    color: isDarkMode ? darkTheme.textPrimary : undefined,
                                                }
                                            }}
                                        />

                                        {/* Textblock Actions */}
                                        {!isAthlete && (
                                            <Box sx={{ 
                                                display: 'flex', 
                                                flexDirection: { xs: 'column', sm: 'row' },
                                                alignItems: { xs: 'stretch', sm: 'center' },
                                                justifyContent: 'space-between',
                                                mt: 2,
                                                pt: 2,
                                                borderTop: `1px solid ${isDarkMode ? darkTheme.cardBorder : grey[200]}`,
                                                flexWrap: { xs: 'nowrap', sm: 'wrap' },
                                                gap: 1.5
                                            }}>
                                                {/* Mobile: Top row with Insert button + Global toggle | Desktop: Left side */}
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    justifyContent: { xs: 'space-between', sm: 'flex-start' },
                                                    gap: 1,
                                                    minWidth: { xs: 0, sm: 'auto' }
                                                }}>
                                                    {/* View textblocks button */}
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{
                                                            textTransform: 'none',
                                                            color: primaryColor,
                                                            borderColor: primaryColor,
                                                            borderRadius: 2,
                                                            px: { xs: 1, sm: 2 },
                                                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                            whiteSpace: 'nowrap',
                                                            minWidth: { xs: 0, sm: 'auto' },
                                                            flexShrink: { xs: 1, sm: 0 },
                                                            '&:hover': { 
                                                                backgroundColor: `${primaryColor}10`,
                                                                borderColor: primaryColor
                                                            }
                                                        }}
                                                        onClick={() => {
                                                            setIndexShow(index);
                                                            setOpenTextblockView(true);
                                                        }}
                                                    >
                                                        Textbaustein einfügen
                                                    </Button>

                                                    {/* Global toggle - Mobile: in first row | Desktop: in right side group */}
                                                    {(profileType === 'nationalCoach') && (
                                                        <Box 
                                                            sx={{ 
                                                                display: { xs: 'flex', sm: 'none' },
                                                                alignItems: 'center', 
                                                                gap: 0.25,
                                                                bgcolor: session.isGlobal 
                                                                    ? (isDarkMode ? `${orange[900]}40` : `${primaryColor}15`) 
                                                                    : (isDarkMode ? darkTheme.headerBg : grey[100]),
                                                                borderRadius: '20px',
                                                                pl: 1,
                                                                pr: 0.5,
                                                                py: 0.25,
                                                                border: `1px solid ${session.isGlobal ? primaryColor : (isDarkMode ? darkTheme.cardBorder : grey[300])}`,
                                                                transition: 'all 0.2s ease',
                                                                flexShrink: 0
                                                            }}
                                                        >
                                                            <PublicOutlined sx={{ 
                                                                fontSize: 14, 
                                                                color: session.isGlobal ? primaryColor : (isDarkMode ? darkTheme.textSecondary : grey[500]),
                                                                transition: 'color 0.2s ease'
                                                            }} />
                                                            <Typography 
                                                                variant="body2" 
                                                                sx={{ 
                                                                    color: session.isGlobal ? primaryColor : (isDarkMode ? darkTheme.textSecondary : grey[600]),
                                                                    fontWeight: session.isGlobal ? 600 : 400,
                                                                    fontSize: '0.7rem',
                                                                    transition: 'all 0.2s ease'
                                                                }}
                                                            >
                                                                Global
                                                            </Typography>
                                                            <Switch
                                                                size="small"
                                                                checked={session.isGlobal || false}
                                                                onChange={event => handleChange(index, event.target.checked)}
                                                                sx={{
                                                                    '& .MuiSwitch-switchBase': {
                                                                        color: isDarkMode ? grey[400] : undefined,
                                                                        '&.Mui-checked': {
                                                                            color: primaryColor,
                                                                            '& + .MuiSwitch-track': {
                                                                                backgroundColor: primaryColor,
                                                                                opacity: 0.5
                                                                            }
                                                                        }
                                                                    },
                                                                    '& .MuiSwitch-track': {
                                                                        backgroundColor: isDarkMode ? grey[700] : grey[400]
                                                                    }
                                                                }}
                                                            />
                                                        </Box>
                                                    )}
                                                </Box>

                                                {/* Desktop: Right side with Global toggle + Save button | Mobile: Bottom row with Save button */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                                    {/* Global toggle - Desktop only */}
                                                    {(profileType === 'nationalCoach') && (
                                                        <Box 
                                                            sx={{ 
                                                                display: { xs: 'none', sm: 'flex' },
                                                                alignItems: 'center', 
                                                                gap: 0.5,
                                                                bgcolor: session.isGlobal 
                                                                    ? (isDarkMode ? `${orange[900]}40` : `${primaryColor}15`) 
                                                                    : (isDarkMode ? darkTheme.headerBg : grey[100]),
                                                                borderRadius: '20px',
                                                                pl: 1.5,
                                                                pr: 0.5,
                                                                py: 0.25,
                                                                border: `1px solid ${session.isGlobal ? primaryColor : (isDarkMode ? darkTheme.cardBorder : grey[300])}`,
                                                                transition: 'all 0.2s ease'
                                                            }}
                                                        >
                                                            <PublicOutlined sx={{ 
                                                                fontSize: 16, 
                                                                color: session.isGlobal ? primaryColor : (isDarkMode ? darkTheme.textSecondary : grey[500]),
                                                                transition: 'color 0.2s ease'
                                                            }} />
                                                            <Typography 
                                                                variant="body2" 
                                                                sx={{ 
                                                                    color: session.isGlobal ? primaryColor : (isDarkMode ? darkTheme.textSecondary : grey[600]),
                                                                    fontWeight: session.isGlobal ? 600 : 400,
                                                                    fontSize: '0.8rem',
                                                                    transition: 'all 0.2s ease'
                                                                }}
                                                            >
                                                                Global
                                                            </Typography>
                                                            <Switch
                                                                size="small"
                                                                checked={session.isGlobal || false}
                                                                onChange={event => handleChange(index, event.target.checked)}
                                                                sx={{
                                                                    '& .MuiSwitch-switchBase': {
                                                                        color: isDarkMode ? grey[400] : undefined,
                                                                        '&.Mui-checked': {
                                                                            color: primaryColor,
                                                                            '& + .MuiSwitch-track': {
                                                                                backgroundColor: primaryColor,
                                                                                opacity: 0.5
                                                                            }
                                                                        }
                                                                    },
                                                                    '& .MuiSwitch-track': {
                                                                        backgroundColor: isDarkMode ? grey[700] : grey[400]
                                                                    }
                                                                }}
                                                            />
                                                        </Box>
                                                    )}
                                                    
                                                    {(textblockId[index] !== undefined) ? (
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            fullWidth={false}
                                                            sx={{
                                                                textTransform: 'none',
                                                                bgcolor: 'darkorange',
                                                                borderRadius: 2,
                                                                px: 2,
                                                                boxShadow: 'none',
                                                                width: { xs: '100%', sm: 'auto' },
                                                                '&:hover': { 
                                                                    bgcolor: '#e68a00',
                                                                    boxShadow: '0 2px 8px rgba(255, 140, 0, 0.3)'
                                                                }
                                                            }}
                                                            onClick={() => handlePutTextblock(index, textblockId[index])}
                                                        >
                                                            Textbaustein aktualisieren
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            fullWidth={false}
                                                            sx={{
                                                                textTransform: 'none',
                                                                bgcolor: 'darkorange',
                                                                borderRadius: 2,
                                                                px: 2,
                                                                boxShadow: 'none',
                                                                width: { xs: '100%', sm: 'auto' },
                                                                '&:hover': { 
                                                                    bgcolor: '#e68a00',
                                                                    boxShadow: '0 2px 8px rgba(255, 140, 0, 0.3)'
                                                                }
                                                            }}
                                                            onClick={() => createTextblock(index, session.isGlobal || false)}
                                                        >
                                                            Als Textbaustein speichern
                                                        </Button>
                                                    )}
                                                </Box>
                                            </Box>
                                        )}
                                    </Paper>
                                </Stack>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>

                {/* Footer Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2, pb: 2 }}>
                    <Button 
                        variant="outlined" 
                        startIcon={<AddOutlined />}
                        onClick={addTrainingSessionEntry}
                        sx={{ 
                            borderRadius: 2, 
                            textTransform: 'none',
                            color: primaryColor,
                            borderColor: primaryColor,
                            '&:hover': {
                                borderColor: primaryColor,
                                backgroundColor: `${primaryColor}10`
                            }
                        }}
                    >
                        Einheit hinzufügen
                    </Button>

                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={<RemoveOutlined />}
                        onClick={() => {
                            if (trainingSessions.length > 1) removeTrainingSession();
                            textblockId.pop();
                            description.pop();
                        }}
                        sx={{ borderRadius: 2, textTransform: 'none' }}
                    >
                        Letzte entfernen
                    </Button>
                </Box>

            </AccordionDetails>

            {/* Textblockdialog */}
            <TextblockView
                textblocks={textblocks}
                setTextblocks={setTextblocks}
                handleSelectTextblock={handleSelectTextblock}
                trainingsSessionIndex={indexShow}
                textblockId={textblockId[indexShow]}
                setTextblockId={(i, val) => (textblockId[i] = val)}
                open={openTextblockView}
                onClose={() => setOpenTextblockView(false)}
            />
        </Accordion>
    );
};

export default CreateWeeklyScheduleSessions;